import React, { useState } from "react";
import { Box, Button, CircularProgress, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { sortByLabel } from "../../utils/sortOptions";

const TYPES = [
  { value: "national", label: "National Holiday" },
  { value: "regional", label: "Regional" },
  { value: "company", label: "Company" },
  { value: "optional", label: "Optional / Floating" },
];

export default function HolidayFormNew() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [type, setType] = useState("company");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/api/holidays/holidays", { name, date, type, description });
      toast.success(`${name} added`);
      nav("/holidays");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  const weekday = date ? new Date(date).toLocaleDateString("en-IN", { weekday: "long" }) : "";

  return (
    <Box p={3}>
      <PageHeader
        title="Add Holiday"
        subtitle="Schedule a new company holiday"
        icon={<CalendarMonthOutlinedIcon />}
        actions={[
          <Button key="back" startIcon={<ArrowBackIcon />} onClick={() => nav(-1)}>Back</Button>,
        ]}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <SectionCard title="Holiday Details" accent="#6C50BF">
            <Box component="form" onSubmit={submit}>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label="Holiday Name"
                  placeholder="e.g. Independence Day"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                <TextField
                  fullWidth
                  type="date"
                  label="Date"
                  InputLabelProps={{ shrink: true }}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  helperText={weekday ? `Falls on a ${weekday}` : "Select a date"}
                />
                <TextField
                  fullWidth
                  select
                  label="Type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  {sortByLabel(TYPES, (t) => t.label).map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Description (optional)"
                  placeholder="Notes visible to employees"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <Button onClick={() => nav("/holidays")}>Cancel</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                    disabled={loading || !name || !date}
                  >
                    {loading ? "Saving…" : "Save Holiday"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <SectionCard title="Preview" accent="#F9BC06">
            <Box
              sx={{
                p: 2.5,
                borderRadius: 3,
                background: "linear-gradient(135deg, #F4EDFA 0%, #EDE8FA 100%)",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ minWidth: 64, textAlign: "center", borderRadius: 2, overflow: "hidden", bgcolor: "#fff", boxShadow: 1 }}>
                  <Box sx={{ bgcolor: "primary.main", color: "#fff", py: 0.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
                      {date ? new Date(date).toLocaleDateString("en-IN", { month: "short" }) : "MMM"}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 26, fontWeight: 800, py: 0.5 }}>
                    {date ? new Date(date).getDate() : "—"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{name || "Holiday name"}</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                    {date ? new Date(date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "Select a date"}
                  </Typography>
                  <Typography sx={{ mt: 1, fontSize: 11, fontWeight: 600, color: "primary.main", textTransform: "capitalize" }}>
                    {TYPES.find((t) => t.value === type)?.label}
                  </Typography>
                </Box>
              </Stack>
              {description && (
                <Typography sx={{ mt: 2, fontSize: 12, color: "text.secondary", fontStyle: "italic" }}>
                  "{description}"
                </Typography>
              )}
            </Box>
            <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 2 }}>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                <b>Tip:</b> Mark as "Optional / Floating" if employees can choose to avail this holiday. Regional holidays can be location-specific.
              </Typography>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
